<template>
  <v-select
    :id="id"
    v-model="selectedVendor"
    :options="vendorOptions"
    :class="error ? 'is-invalid' : ''"
    placeholder="Select Vendor Code"
    :loading="loadingVendors || loadingSelectedVendor"
    :clearable="true"
    @search="onSearch"
    @input="handleInput"
    label="code"
    :disabled="disabled"
  >
    <template #no-options>
      <div class="text-center text-muted">No vendor codes found</div>
    </template>

    <template #spinner="{ loading }">
      <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </template>

    <template #selected-option="{ code, company }">
      <div class="d-flex flex-column">
        <span class="font-weight-bold">{{ code }}</span>
        <small class="text-muted">{{ company }}</small>
      </div>
    </template>

    <template #option="{ code, company }">
      <div class="d-flex flex-column">
        <span class="font-weight-bold">{{ code }}</span>
        <small class="text-muted">{{ company }}</small>
      </div>
    </template>
  </v-select>
</template>

<script>
import { APIService } from '@/utils/api';
import { RESOURCES } from '@/utils/constants';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';

export default {
  name: 'VendorCodeDropdown',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
  },
  props: {
    // Unique identifier for the input
    id: {
      type: String,
      default: () => `vendor-code-${Math.random().toString(36).substr(2, 9)}`,
    },
    // Input label
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: 'vendorCode',
    },
    // v-model value - should be vendor ID
    value: {
      type: [Number, String],
      default: null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: '',
      loadingVendors: false,
      loadingSelectedVendor: false,
      vendorOptions: [],
      selectedVendor: null,
    };
  },
  computed: {
    userID() {
      return this.$store.state.user?.id || 1;
    },
    businessID() {
      return this.$store.state.user?.businessID || 1;
    },
  },
  watch: {
    value: {
      async handler(newVal) {
        if (newVal === null || newVal === 0 || newVal === '' || newVal === undefined) {
          this.selectedVendor = null;
        } else if (newVal !== (this.selectedVendor ? this.selectedVendor.id : null)) {
          await this.fetchVendorById(newVal);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchVendors();
  },
  methods: {
    // Transform getById API response to match getByPagination format
    transformVendorData(apiResponse) {
      if (!apiResponse || !apiResponse.vendor) return null;

      const { vendor, primaryContact, phones, addresses } = apiResponse;

      // Extract primary phone number
      const primaryPhone =
        phones && phones.length > 0
          ? phones.find((phone) => phone.isPrimary)?.phoneNumber || phones[0]?.phoneNumber || ''
          : '';

      // Extract city from main address
      const mainAddress =
        addresses && addresses.length > 0
          ? addresses.find((addr) => addr.addressType === 'Main') || addresses[0]
          : null;

      return {
        id: vendor.id,
        code: vendor.vendorCode || '',
        company: vendor.companyName || '',
        primaryContact: {
          name: primaryContact?.fullName || '',
          phone: primaryPhone,
        },
        city: mainAddress ? '' : '', // City info not available in addresses, would need city lookup
        type: `TypeID: ${vendor.vendorTypeID || ''}`,
        accountOfficer: null, // Not available in getById response
        status: vendor.isActive ? 'Active' : 'Inactive',
        group: `CategoryID: ${vendor.vendorCategoryID || ''}`,
        createdAt: {
          date: vendor.addedOn
            ? new Date(vendor.addedOn).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '',
          time: vendor.addedOn
            ? new Date(vendor.addedOn).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
            : '',
          user: null,
        },
        updatedAt: {
          date: vendor.updatedOn
            ? new Date(vendor.updatedOn).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '',
          time: vendor.updatedOn
            ? new Date(vendor.updatedOn).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
            : '',
          user: vendor.metaUpdatedBy || null,
        },
        metaAddedBy: vendor.metaAddedBy,
        metaUpdatedBy: vendor.metaUpdatedBy,
        // Keep original data for reference
        _originalData: apiResponse,
      };
    },

    async fetchVendorById(vendorId) {
      if (!vendorId || vendorId === '0') {
        this.selectedVendor = null;
        return;
      }

      this.loadingSelectedVendor = true;
      try {
        const response = await APIService.getById(
          RESOURCES.VENDORS,
          vendorId,
          this.businessID,
          this.userID
        );

        if (response.data && response.data.status === 'success' && response.data.data) {
          const transformedVendor = this.transformVendorData(response.data.data);

          if (transformedVendor) {
            this.selectedVendor = transformedVendor;

            // Add to options if not already present
            if (!this.vendorOptions.find((opt) => opt.id === transformedVendor.id)) {
              this.vendorOptions.unshift(transformedVendor);
            }
          } else {
            console.error('Failed to transform vendor data:', response.data.data);
            this.selectedVendor = null;
          }
        } else {
          console.error('Error fetching vendor by ID:', response.data);
          this.selectedVendor = null;
        }
      } catch (error) {
        console.error('Error fetching vendor by ID:', error);
        this.selectedVendor = null;
      } finally {
        this.loadingSelectedVendor = false;
      }
    },

    async fetchVendors() {
      this.loadingVendors = true;
      try {
        const response = await APIService.getByPagination(RESOURCES.VENDORS, this.businessID, {
          vendorCode: this.searchTerm,
          pageSize: 20,
          pageNumber: 1,
          sortField: 'code',
          sortOrder: 'asc',
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          this.vendorOptions = [...response.data.data.items];
        } else {
          console.error('Error in paginated vendor data format:', response.data);
          this.vendorOptions = [];
        }
      } catch (error) {
        console.error('Error fetching vendors:', error);
        this.vendorOptions = [];
      } finally {
        this.loadingVendors = false;
      }
    },

    onSearch(searchTerm) {
      this.searchTerm = searchTerm;
      this.fetchVendors();
    },

    handleInput(value) {
      this.selectedVendor = value;

      // Emit vendor ID for v-model (input event)
      this.$emit('input', value ? value.id : null);

      // Emit full vendor object for 'selected' event
      this.$emit('selected', value);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  ::v-deep {
    .vs__dropdown-option {
      padding: 8px 12px;

      &--highlight {
        background: #f8f9fa;
        color: #2c3e50;
      }
    }

    .vs__dropdown-menu {
      max-height: 350px;
    }

    .font-weight-bold {
      font-weight: 600;
    }
  }
}
</style>
